import { FC, ReactElement, useCallback, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Button, Stack } from "@mui/material";

import { useSearchQuery } from "@/hooks";

interface IRouterTab {
  text: string;
  link: string;
}

interface IQueryTab {
  text: string;
  value: string;
}

type RouterTabsProps =
  | {
      tabs: IRouterTab[];
      isQuery?: undefined;
      queryName?: undefined;
    }
  | {
      tabs: IQueryTab[];
      isQuery: true;
      queryName: string;
    };

const RouterTabs: FC<RouterTabsProps> = ({
  tabs,
  isQuery = false,
  queryName,
}): ReactElement => {
  const { pathname } = useLocation();
  const { getQueryValue, updateQuery } = useSearchQuery();

  const endRoute = useMemo(() => {
    return pathname.slice(pathname.lastIndexOf("/") + 1);
  }, [pathname]);

  const addQuery = useCallback(
    (value: string) => {
      if (queryName) {
        updateQuery({ [queryName]: value });
      }
    },
    [queryName, updateQuery]
  );

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {tabs.map((tab, index) => {
        return (
          <Button
            {...(!isQuery
              ? { component: RouterLink, to: (tab as IRouterTab).link }
              : {
                  onClick: () => addQuery((tab as IQueryTab).value),
                })}
            variant={
              (
                isQuery
                  ? getQueryValue(queryName || "", "") ===
                    (tab as IQueryTab).value
                  : endRoute === (tab as IRouterTab).link
              )
                ? "primary-contained"
                : "white-outlined"
            }
            key={`${(tab as IRouterTab).link}-${index + 1}`}
            sx={{
              borderRadius: 4,
              px: 2,
              py: 1,
              fontSize: 16,
              fontWeight: 500,
              lineHeight: "22px",
              minHeight: "auto",
            }}
          >
            {tab.text}
          </Button>
        );
      })}
    </Stack>
  );
};

export default RouterTabs;
